import { useMemo } from 'react';
import { Props as SelectProps } from 'react-select';
import { useTheme, DefaultTheme } from '@material-ui/styles';
import { useTimeZones } from '@bizzabo/entities';
import { Select } from '@bizzabo/design-system';
import Control from './Control';
import SingleValue from './SingleValue';

const formatGroupLabel = data => (
    <div style={groupStyles}>
        <span>{data.label}</span>
    </div>
);

export interface TimezonePickerPropsType {
    value?: string;
    onChange?: SelectProps['onChange'];
    placeholder?: SelectProps['placeholder'];
    className?: string;
}

const QUICK_SELECT = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Copenhagen',
    'Asia/Jerusalem',
    'Asia/Shanghai',
    'Asia/Calcutta',
    'Australia/Sydney',
];

const TimezonePicker: React.FC<TimezonePickerPropsType> = ({
    value,
    onChange = () => {},
    placeholder = 'Timezone',
    className = '',
}) => {
    const { data: timeZoneResponse, isSuccess } = useTimeZones();
    const allTimezones = isSuccess ? timeZoneResponse : [];

    const theme = useTheme<DefaultTheme>();
    const textStyle = {
        ...theme.typography.t71,
        color: theme.customization.interactive.G50,
    };
    const customStyles: SelectProps['styles'] = {
        menu: provided => ({
            ...provided,
            ...theme.typography.t61,
            color: theme.customization.interactive.G50,
            width: '300px',
        }),
        menuPortal: provided => ({
            ...provided,
            zIndex: 10003,
        }),
        indicatorSeparator: () => ({}), // hide the separator
        control: provided => ({
            ...provided,
            padding: '0 12px',
            border: 0,
            justifyContent: 'space-between',
            boxShadow: 'none', // This line disables the blue border
            minWidth: 160,
            width: 'max-content',
        }),
        dropdownIndicator: provided => ({
            ...provided,
            color: theme.customization.interactive.G50,
            padding: '6px 0px',
        }),
        valueContainer: provided => ({
            ...provided,
            flex: 'none', // see justifyContent note above
            paddingRight: 0,
            paddingLeft: 0,
        }),
        singleValue: provided => ({
            ...provided,
            ...textStyle,
            position: 'static', // see justifyContent note above
            transform: 'none',
        }),
        placeholder: provided => ({
            ...provided,
            ...textStyle,
            position: 'static',
            transform: 'none',
        }),
        input: provided => ({ ...provided, ...theme.typography.t81 }),
    };

    const allTimeZoneOptions = allTimezones.map(timeZone => ({
        ...timeZone,
        value: timeZone.name,
        label: `${timeZone.alternativeName} (${timeZone.countryName})`,
    }));

    const quickGroupOptions = {
        label: 'Quick Select',
        options: allTimeZoneOptions.filter(option => QUICK_SELECT.includes(option.name)),
    };
    const allGroupOptions = {
        label: 'All',
        options: allTimeZoneOptions.filter(option => !QUICK_SELECT.includes(option.name)),
    };
    const groupedOptions = [quickGroupOptions, allGroupOptions];
    const selectValue = useMemo(
        () =>
            groupedOptions
                .find(optionGroup => optionGroup.options.find(option => option.value === value))
                ?.options.find(option => option.value === value),
        [groupedOptions, value]
    );

    return (
        <Select
            className={className}
            value={selectValue}
            options={groupedOptions}
            components={{ SingleValue, Control }}
            styles={customStyles}
            onChange={onChange}
            placeholder={placeholder}
            formatGroupLabel={formatGroupLabel}
        />
    );
};

export const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

export default TimezonePicker;
